<template>
  <BuilderWrapper
    title="Info Card Settings"
  >
    <LabelledInput label="Show">
      <b-checkbox
        v-model="showInfoCard"
        switch
        data-test="show-info-card"
      />
    </LabelledInput>

    <LabelledInput label="Info Card Colour">
      <ColorPicker
        :value="infoCardColour"
        @input="updateInfoCardColour($event)"
      />
    </LabelledInput>

    <LabelledInput label="Info Card Opacity">
      <b-input
        id="infoCardOpacity"
        v-model="infoCardOpacity"
        type="range"
        number
        min="0"
        max="1"
        step="0.05"
      />
      <b-tooltip
        target="infoCardOpacity"
        triggers="hover"
      >
        {{ infoCardOpacityPercentageText }}
      </b-tooltip>
    </LabelledInput>

    <LabelledInput label="Content Panels Colour">
      <ColorPicker
        :value="contentPanelsColour"
        @input="updateContentPanelsColour($event)"
      />
    </LabelledInput>

    <LabelledInput label="Content Panels Opacity">
      <b-input
        id="contentPanelsOpacity"
        v-model="contentPanelsOpacity"
        type="range"
        number
        min="0"
        max="1"
        step="0.05"
      />
      <b-tooltip
        target="contentPanelsOpacity"
        triggers="hover"
      >
        {{ contentPanelsOpacityPercentageText }}
      </b-tooltip>
    </LabelledInput>

    <SizeInput :size.sync="size" />

    <InfoCardContentPanels :tab-node="tabNode" />
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import InfoCardContentPanels from '@/components/builder/CustomVisualisationSettings/InfoCardContentPanels.vue'
import SizeInput from '@/components/builder/CustomVisualisationSettings/SizeInput.vue'
import { ColorPicker } from '@orbica/vue-modules'

import { CustomTabConfigKeys, CustomTabNode, InfoCardSize } from '@/types'
import { NODE_CONFIG_KEY, DEFAULT_INFO_CARD_SIZE, DEFAULT_INFO_CARD_OPACITY, DEFAULT_INFO_CARD_COLOUR } from '@/constants'

const components = {
  BuilderWrapper,
  LabelledInput,
  InfoCardContentPanels,
  SizeInput,
  ColorPicker,
}

const props = {
  tabNode: Object as PropType<CustomTabNode>,
}

@Component({ components })
export default class InfoCardSettings extends Vue.extend({ props }) {
  get showInfoCard () {
    return this.tabNode.config?.showInfoCard ?? false
  }

  set showInfoCard (value) {
    this.updateNodeConfigProp(CustomTabConfigKeys.SHOW_INFO_CARD, value)
  }

  get size () {
    return this.tabNode.config?.infoCardSize ?? DEFAULT_INFO_CARD_SIZE
  }

  set size (value: InfoCardSize) {
    this.updateNodeConfigProp(CustomTabConfigKeys.INFO_CARD_SIZE, value)
  }

  get infoCardOpacity () {
    return this.tabNode.config?.infoCardOpacity ?? DEFAULT_INFO_CARD_OPACITY
  }

  set infoCardOpacity (value: number) {
    this.updateNodeConfigProp(CustomTabConfigKeys.INFO_CARD_OPACITY, value)
  }

  get infoCardOpacityPercentageText () {
    return this.opacityPercentageText(this.infoCardOpacity)
  }

  get infoCardColour () {
    return this.tabNode.config?.infoCardColour ?? DEFAULT_INFO_CARD_COLOUR
  }

  get contentPanelsColour () {
    return this.tabNode.config?.infoCardContentPanelsColour ?? DEFAULT_INFO_CARD_COLOUR
  }

  get contentPanelsOpacity () {
    return this.tabNode.config?.infoCardContentPanelsOpacity ?? 0
  }

  set contentPanelsOpacity (value: number) {
    this.updateNodeConfigProp(CustomTabConfigKeys.INFO_CARD_CONTENT_PANELS_OPACITY, value)
  }

  get contentPanelsOpacityPercentageText () {
    return this.opacityPercentageText(this.contentPanelsOpacity)
  }

  opacityPercentageText (opacity: number) {
    return `${(opacity * 100).toFixed()}%`
  }

  updateInfoCardColour (value: string) {
    this.updateNodeConfigProp(CustomTabConfigKeys.INFO_CARD_COLOUR, value)
  }

  updateContentPanelsColour (value: string) {
    this.updateNodeConfigProp(CustomTabConfigKeys.INFO_CARD_CONTENT_PANELS_COLOUR, value)
  }

  updateNodeConfigProp (propName: string, value: any) {
    this.$typedStore.primary.entities.nodes.updateNodeProp({
      path: this.tabNode.path,
      configPath: [NODE_CONFIG_KEY, propName],
      value,
    })
  }
}

</script>
